import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {Point} from '../../../components/layout/abstract-units';
import {Cell} from '../../../components/layout/cell';
import {CellArea} from '../../../components/layout/docking/cell-area';
import {Edge} from '../../../components/layout/edge';
import {Guideline} from '../../../components/layout/guideline';
import {Layout} from '../../../components/layout/layout';
import {LayoutChanges} from '../../../components/layout/layout-changes';
import {LayoutSnapshot} from '../../../components/layout/layout-snapshot';
import {Wireframe} from '../../../components/layout/wireframe';
import {PositionDetectorDirective} from '../../../components/position-detector';

export enum LayoutUpdateReason {
    RESIZE,
    LOCK,
    UNLOCK,
    BLUR,
    UNBLUR,
    CHANGE
}

export enum LayoutState {
    NORMAL,
    RESIZING_X,
    RESIZING_Y,
    MOVING,
    SETTINGS
}

export interface LayoutUpdateEvent {
    reason: LayoutUpdateReason;
    cells?: Cell[]; // Falsy value is considered as all cells.
    changes?: LayoutChanges; // Defined for LayoutUpdateReason.CHANGE only.
}

export interface LayoutSharedServicePrivateExports {
    updateSubject$: Subject<LayoutUpdateEvent>;
    setLayout: (layout: Layout) => void;
}

export interface LayoutSharedServiceRpc {
    collectDisplayGuidelines: () => void;
    setLayoutState: (layoutState: LayoutState) => void;
    getLayoutSize: () => Point;
    getInnerPosDetector: () => PositionDetectorDirective;
}

@Injectable()
export class LayoutSharedService {

    public get layout(): Layout {
        return this._layout;
    }

    public readonly update$: Observable<LayoutUpdateEvent>;

    private _layout: Layout;

    private readonly updateSubject$ = new Subject<LayoutUpdateEvent>();

    private rpc: LayoutSharedServiceRpc;

    constructor() {
        this.update$ = this.updateSubject$.asObservable();
    }

    public init(rpc: LayoutSharedServiceRpc): LayoutSharedServicePrivateExports {
        this.rpc = rpc;

        return {
            updateSubject$: this.updateSubject$,
            setLayout: layout => this._layout = layout
        };
    }

    public getLayoutSize(): Point {
        return this.rpc.getLayoutSize();
    }

    public getInnerPosDetector(): PositionDetectorDirective {
        return this.rpc.getInnerPosDetector();
    }

    public setWireframes(wireframes: Wireframe[]): void {
        this.layout.setWireframes(wireframes);
    }

    public setGuidelines(guidelines: Guideline[]): void {
        this.layout.setGuidelines(guidelines);
        this.rpc.collectDisplayGuidelines();
    }

    public lock(): void {
        this.updateSubject$.next({reason: LayoutUpdateReason.LOCK});
    }

    public unlock(): void {
        this.updateSubject$.next({reason: LayoutUpdateReason.UNLOCK});
    }

    public blur(cells?: Cell[]): void {
        this.updateSubject$.next({reason: LayoutUpdateReason.BLUR, cells});
    }

    public blurOthers(exclusionCells: Cell | Cell[]): void {
        this.blur(this.layout.getOtherCells(exclusionCells));
    }

    public unblur(cells?: Cell[]): void {
        this.updateSubject$.next({reason: LayoutUpdateReason.UNBLUR, cells});
    }

    public unblurOthers(exclusionCells: Cell | Cell[]): void {
        this.unblur(this.layout.getOtherCells(exclusionCells));
    }

    public setEdgeDisplayMovingAxisPos(edge: Edge, newDisplayMovingAxisPos: number): void {
        const changes: LayoutChanges = this.layout.edgeActions
            .setEdgeDisplayMovingAxisPos(edge, newDisplayMovingAxisPos);

        this.layout.commitChanges(changes);
    }

    public moveCell(cell: Cell, targetArea: CellArea): void {
        const changes: LayoutChanges = this.layout.cellActions.moveCell(cell, targetArea);

        this.layout.commitChanges(changes);
    }

    public createLayoutSnapshot(): LayoutSnapshot {
        return new LayoutSnapshot(this.layout);
    }

    public restoreLayoutFromSnapshot(layoutSnapshot: LayoutSnapshot): void {
        layoutSnapshot.restoreLayout(this.layout);
    }

    public removeCell(cell: Cell): void {
        const changes: LayoutChanges = this.layout.cellActions.removeCell(cell);

        this.layout.commitChanges(changes);
    }

    public setLayoutState(layoutState: LayoutState): void {
        this.rpc.setLayoutState(layoutState);
    }

}
